import styled, { css, keyframes } from 'styled-components';
import type { WorkflowStageTone } from '../../../hooks/useWorkflowStages';

export const StepperBar = styled.div`
  display: flex;
  width: 100%;
  height: 54px;
  padding: 0 16px;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  background: var(--mantine-color-body);
  border-bottom: 1px solid light-dark(#e9ecef, var(--mantine-color-dark-5));
  /* No overflow at the bar level — only the inner StepsRow scrolls, so the
     back/next arrows stay pinned at the edges regardless of viewport width. */
  overflow: hidden;
  position: sticky;
  /* Pinned just below the AppTopBar (47px tall, sticky at top: 0), and below the run band when one
     is up — it sets the --dx-run-band custom property while it is on screen and clears it after. */
  top: calc(47px + var(--dx-run-band, 0px));
  z-index: 9;
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: light-dark(#909296, var(--mantine-color-dark-2));
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    background: light-dark(#f1f3f5, var(--mantine-color-dark-6));
    color: #228be6;
  }

  &:disabled {
    color: light-dark(#ced4da, var(--mantine-color-dark-3));
    cursor: not-allowed;
  }
`;

/**
 * Full-height vertical divider that separates the back/forward arrows from
 * the steps row. Stretches the entire stepper height by aligning to the
 * `StepperBar` flex line (which has `align-items: center` for the rest of
 * the chrome but lets stretch children take the full track via align-self).
 */
export const ArrowDivider = styled.span`
  display: inline-block;
  width: 1px;
  align-self: stretch;
  background: light-dark(#e9ecef, var(--mantine-color-dark-5));
  flex-shrink: 0;
`;

export const StepsRow = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  /* Horizontal scroll inside the back/next arrows when the viewport is too
     narrow to lay every step out side-by-side. Steps keep their natural
     width — they no longer compress into illegible chips. */
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: light-dark(#dee2e6, var(--mantine-color-dark-4));
    border-radius: 2px;
  }
`;

export const StepItemWrap = styled.li<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  /* Grow to fill the row's spare width (the Connector inside each wrap
     absorbs the extra space, stretching evenly between labels) but never
     shrink below the wrap's natural width. With flex-shrink=0 the labels
     keep their full width at every viewport; StepsRow's overflow-x then
     handles the case where natural width > container. The last wrap has
     no Connector after it, so it stays at natural width to avoid trailing
     whitespace at the right edge of the row. */
  flex: ${({ $compact }) => ($compact ? '0 0 auto' : '1 0 auto')};

  &:last-child {
    flex: 0 0 auto;
  }
`;

const toneColors = {
  success: '#12b886',
  info: '#228be6',
  warning: '#fab005',
  error: '#fa5252',
  neutral: '#adb5bd'
} as const;

export const StepButton = styled.button<{ $disabled?: boolean; $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  text-align: left;
  min-width: 0;
  font: inherit;
  transition: background 0.15s ease;

  @media (max-width: 1199px) {
    padding: 6px 4px;
  }

  > .label {
    color: light-dark(#101113, var(--mantine-color-dark-0));
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 140%;
    white-space: nowrap;
  }

  /* Sub-label typography now lives on the SubLabel styled-component
     itself. The shortcut-icon feature nests SubLabel inside SubLabelRow,
     so a direct-child selector for .sub-label can't reach it here. */

  &:not(:disabled):hover > .label {
    text-decoration: underline;
  }
`;

/* The light travelling across the step that is working. Slow and low-contrast on purpose: it sits
   in the chrome above every page, and a stepper that pulses hard is a stepper nobody can read past. */
const shimmer = keyframes`
  from { background-position: 160% 0; }
  to { background-position: -60% 0; }
`;

/* A stage with nothing to count — copying funds, generating a report — still has to look like it is
   working, so the ring goes round on its own instead of standing at a number it does not have. */
const sweep = keyframes`
  from { --dx-step-progress: 0%; }
  to { --dx-step-progress: 100%; }
`;

export const StepHead = styled.div<{
  $active?: boolean;
  $running?: boolean;
  $indeterminate?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  /* Active pill — soft gray rounded rectangle wrapping the whole step. */
  ${({ $active }) =>
    $active &&
    css`
      background: light-dark(#f1f3f5, var(--mantine-color-dark-6));
      box-shadow: inset 0 0 0 1px light-dark(#e9ecef, var(--mantine-color-dark-5));

      > span > button {
        background: transparent;
      }
    `}

  /* The step a run is on. It takes the pill over from the active one: the two can be different steps
     when the reader has walked off on their own, and the one that is working is the one that should
     be unmistakable. */
  ${({ $running, $indeterminate }) =>
    $running &&
    css`
      background: light-dark(#f1f8ff, var(--mantine-color-dark-5));
      box-shadow: none;

      > button {
        position: relative;
        z-index: 1;
        background: transparent;
      }

      /* How far round the stage has got. A conic sweep rather than a stroke walking the perimeter:
         it is one element and one custom property, it survives any chip width, and read at a glance
         it says the same thing — the line is going round, and when it closes the stage is done. */
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 0;
        border-radius: 8px;
        padding: 1.5px;
        background: conic-gradient(
          from -90deg,
          #228be6 var(--dx-step-progress, 0%),
          rgba(34, 139, 230, 0.16) 0
        );
        -webkit-mask:
          linear-gradient(#000 0 0) content-box,
          linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask:
          linear-gradient(#000 0 0) content-box,
          linear-gradient(#000 0 0);
        mask-composite: exclude;
        transition: --dx-step-progress 0.6s linear;
        pointer-events: none;
        ${
          $indeterminate &&
          css`
            animation: ${sweep} 2.4s linear infinite;
          `
        }
      }

      &::after {
        content: '';
        position: absolute;
        inset: 1px;
        z-index: 0;
        border-radius: 7px;
        background-image: linear-gradient(
          100deg,
          rgba(34, 139, 230, 0) 38%,
          rgba(34, 139, 230, 0.14) 50%,
          rgba(34, 139, 230, 0) 62%
        );
        background-size: 220% 100%;
        animation: ${shimmer} 1.9s linear infinite;
        pointer-events: none;
      }

      @media (prefers-reduced-motion: reduce) {
        &::before,
        &::after {
          animation: none;
        }

        /* A stage with nothing to count draws its ring from a value the animation was supplying.
           Stop the animation and the value falls back to its registered 0%, so the ring disappears
           and a stage that is copying funds or generating a report looks exactly like one that is
           not running. Give it the whole ring instead, paler, so it reads as on rather than empty. */
        ${
          $indeterminate &&
          css`
            &::before {
              --dx-step-progress: 100%;
              opacity: 0.55;
            }
          `
        }
      }
    `}
`;

export const LockedStepWrap = styled.span`
  display: inline-flex;
`;

interface ConnectorProps {
  $tone: 'done' | 'pending';
  // Few steps (an approver's two): a short fixed line instead of one stretched across the bar.
  $compact?: boolean;
}

export const Connector = styled.span<ConnectorProps>`
  display: inline-block;
  /* Grows to absorb the row's slack at wide widths, never shrinks below
     12px. Combined with StepsRow's overflow-x, the row keeps a sensible
     look at every width: stretched when there's room, scrollable when not.
     Narrow + tight margins keep adjacent step labels visually close. */
  flex: ${({ $compact }) => ($compact ? '0 0 48px' : '1 1 12px')};
  min-width: 12px;
  height: 1px;
  margin: 0 4px;
  background: ${({ $tone }) => ($tone === 'done' ? toneColors.info : 'light-dark(#dee2e6, var(--mantine-color-dark-4))')};
  ${({ $tone }) =>
    $tone === 'pending' &&
    css`
      background-image: linear-gradient(
        to right,
        light-dark(#dee2e6, var(--mantine-color-dark-4)) 50%,
        transparent 50%
      );
      background-size: 8px 1px;
      background-repeat: repeat-x;
      background-color: transparent;
    `}
`;

export const SubLabel = styled.span<{ $tone?: WorkflowStageTone }>`
  font-family: Roboto, sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 140%;
  white-space: nowrap;
  color: ${({ $tone }) => {
    if ($tone === 'success') return toneColors.success;
    if ($tone === 'warning') return toneColors.warning;
    if ($tone === 'info') return toneColors.info;
    if ($tone === 'error') return toneColors.error;
    return '#868e96';
  }};
`;

// Inline row that pairs the sub-label text with any per-stage shortcut
// button (e.g. "Copy jurisdictions from previous period"). Keeps the icon
// vertically centred with the sub-label baseline.
export const SubLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid light-dark(#dee2e6, var(--mantine-color-dark-4));
  border-radius: 4px;
  background: var(--mantine-color-body);
  color: ${toneColors.info};
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    background: light-dark(#e7f5ff, var(--mantine-color-dark-5));
    border-color: ${toneColors.info};
    color: light-dark(#1c7ed6, var(--mantine-color-blue-4));
    box-shadow: 0 1px 2px rgba(34, 139, 230, 0.15);
  }

  &:active:not(:disabled) {
    background: light-dark(#d0ebff, var(--mantine-color-dark-4));
    box-shadow: inset 0 1px 2px rgba(34, 139, 230, 0.2);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;
